/*
 * The drive's screens, each a chunk of its own, in one table the routes and the item dispatcher read
 * (`routes.tsx`, `open.tsx`). The route table says *where* a page is; this file says *which component*
 * draws it, so a page's package changes one line here and nothing in the routes.
 *
 * Everything here is a `lazy()` of an `import()` named by a literal specifier: this file is reachable
 * from the entry chunk through the module object, and a static import of a screen would move it there
 * (`core/modules/entryClosure.test.ts`, `scripts/checkBudgets.ts`).
 */

import { lazy, type ComponentType } from "react"
import type { DocLinkRedeemed } from "../../core/docs/wire/docs.gen.ts"
import type { Resolved } from "./wire/drive.gen.ts"

/** What a screen of an address that names something is handed: what the key resolved to. */
export interface ResolvedScreenProps {
	resolved: Resolved
}

/** Recent, where `/drive` opens. */
export const RecentScreen = lazy(async () => ({
	default: (await import("./home/RecentPage.tsx")).RecentPage,
}))

/** The reader's My drive: the folder page at its root. */
export const MyDriveScreen = lazy(async () => ({
	default: (await import("./spaces/MyDrivePage.tsx")).MyDrivePage,
}))

/** The reader's starred items. */
export const StarredScreen = lazy(async () => ({
	default: (await import("./spaces/StarredPage.tsx")).StarredPage,
}))

/** The trash. */
export const TrashScreen = lazy(async () => ({
	default: (await import("./trash/TrashScreen.tsx")).TrashScreen,
}))

/** A space's root or a folder. */
export const FolderScreen: ComponentType<ResolvedScreenProps> = lazy(async () => ({
	default: (await import("./folder/FolderPage.tsx")).FolderPage,
}))

/** A document: the drive's document page around core's view. */
export const DocumentScreen: ComponentType<ResolvedScreenProps> = lazy(async () => ({
	default: (await import("./document/DocumentPage.tsx")).DocumentPage,
}))

/**
 * A document opened by its link by a signed-in person outside its drive (DV W-1 §3.7): the document
 * page without the drive's place, for what the redeem answered.
 */
export const LinkedDocumentScreen: ComponentType<{ linked: DocLinkRedeemed }> = lazy(async () => ({
	default: (await import("./document/DocumentPage.tsx")).LinkedDocumentPage,
}))

/** A file and its preview. */
export const FileScreen: ComponentType<ResolvedScreenProps> = lazy(async () => ({
	default: (await import("./files/FileScreen.tsx")).FileScreen,
}))

/** The administrators' Other shared drives. */
export const AdminScreen = lazy(async () => ({
	default: (await import("./admin/OtherSharedDrivesPage.tsx")).OtherSharedDrivesPage,
}))

/** What an address under `/drive/<key>` resolves through: the dispatcher of `open.tsx`. */
export const OpenScreen = lazy(async () => ({ default: (await import("./open.tsx")).OpenItem }))

/** The drive's dead end: a malformed key, an unknown one and one the reader cannot open. */
export const NoAccessScreen = lazy(async () => ({ default: (await import("./open.tsx")).NoAccess }))
